import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Download,
  Package,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function AdminSapByClientPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [onlyPending, setOnlyPending] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sap-by-client", {
        params: { only_pending: onlyPending },
      });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [onlyPending]);

  const totals = useMemo(() => {
    const t = rows.reduce(
      (acc, r) => {
        acc.batches += r.total_batches;
        acc.docs += r.total_docs;
        acc.pending += r.pending_batches;
        acc.pending_docs += r.pending_docs;
        return acc;
      },
      { batches: 0, docs: 0, pending: 0, pending_docs: 0 }
    );
    t.clients = rows.length;
    return t;
  }, [rows]);

  const downloadOne = async (row) => {
    setDownloadingId(row.user_id);
    try {
      const res = await api.get(
        `/admin/sap-by-client/${row.user_id}/download`,
        {
          responseType: "blob",
          params: { only_pending: onlyPending },
        }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SAP_${(row.id_cliente || row.razon_social || row.name || "cliente").replace(
        /\s+/g,
        "_"
      )}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`SAP descargado: ${row.razon_social || row.name}`);
      load();
    } catch (e) {
      const msg =
        e.response?.data instanceof Blob
          ? await e.response.data.text().then((t) => {
              try {
                return JSON.parse(t).detail;
              } catch {
                return t;
              }
            })
          : formatApiError(e.response?.data?.detail);
      toast.error(msg || "Error al descargar");
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadAll = async () => {
    if (rows.length === 0) {
      toast.error("No hay clientes con lotes para descargar");
      return;
    }
    setDownloadingAll(true);
    try {
      const res = await api.get("/admin/sap-by-client/download-all", {
        responseType: "blob",
        params: { only_pending: onlyPending },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[-:T]/g, "");
      a.download = `SAP_EXIS_PHARMA_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP descargado con todos los SAP por cliente");
      load();
    } catch (e) {
      const msg =
        e.response?.data instanceof Blob
          ? await e.response.data.text().then((t) => {
              try {
                return JSON.parse(t).detail;
              } catch {
                return t;
              }
            })
          : formatApiError(e.response?.data?.detail);
      toast.error(msg || "Error al descargar ZIP");
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 md:p-10">
        <PageHeader
          eyebrow="Administración · Distribución SAP"
          title="SAP por Cliente"
          description="Genera y descarga un archivo Excel SAP por cada empresa cliente. Cada cliente = un archivo consolidado con todos sus lotes."
          actions={
            <button
              onClick={downloadAll}
              disabled={downloadingAll || rows.length === 0}
              data-testid="sap-download-all-btn"
              className="flex items-center gap-2 px-5 py-3 bg-[#D95A40] hover:bg-[#c94d33] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-[0.15em] text-xs transition-colors"
            >
              {downloadingAll ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Generando ZIP...
                </>
              ) : (
                <>
                  <Package className="w-4 h-4" /> Descargar todos (ZIP)
                </>
              )}
            </button>
          }
        />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat
            label="Clientes"
            value={totals.clients}
            icon={<Package className="w-4 h-4" />}
          />
          <Stat
            label={onlyPending ? "Lotes pendientes" : "Lotes totales"}
            value={onlyPending ? totals.pending : totals.batches}
            icon={
              onlyPending ? (
                <Clock className="w-4 h-4" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )
            }
            highlight={onlyPending && totals.pending > 0}
          />
          <Stat
            label={onlyPending ? "Documentos pendientes" : "Documentos totales"}
            value={onlyPending ? totals.pending_docs : totals.docs}
            icon={<FileSpreadsheet className="w-4 h-4" />}
          />
          <div className="bg-white border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
                Filtro
              </div>
              <div className="text-sm text-[#0B1B3D] font-semibold">
                {onlyPending ? "Solo pendientes" : "Todos los lotes"}
              </div>
            </div>
            <Switch
              checked={onlyPending}
              onCheckedChange={setOnlyPending}
              data-testid="sap-only-pending-toggle"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B1B3D] text-white">
              <tr>
                <Th>Empresa</Th>
                <Th>RUT</Th>
                <Th>ID Cliente</Th>
                <Th className="text-right">Lotes</Th>
                <Th className="text-right">Docs</Th>
                <Th className="text-right">Pendientes</Th>
                <Th>Último envío</Th>
                <Th>Estado</Th>
                <Th className="text-right">Acción</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={9} className="p-10 text-center text-slate-500">
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-10 text-center text-slate-500">
                    {onlyPending
                      ? "No hay clientes con lotes pendientes."
                      : "No hay clientes con lotes registrados."}
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.user_id}
                  data-testid={`sap-client-row-${r.user_id}`}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0B1B3D]">
                      {r.razon_social || r.name}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {r.email}
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-slate-700 font-mono text-xs">
                    {r.rut || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {r.id_cliente || "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-display font-bold text-[#0B1B3D] tabular-nums">
                    {r.total_batches}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.total_docs}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.pending_batches > 0 ? (
                      <span className="inline-block bg-[#D95A40]/10 text-[#D95A40] px-2 py-0.5 font-bold">
                        {r.pending_batches}
                      </span>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                    {r.last_upload_at
                      ? new Date(r.last_upload_at).toLocaleString("es-CL")
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {r.pending_batches === 0 && r.total_batches > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Al día
                      </span>
                    ) : r.pending_batches > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-[#D95A40] font-semibold">
                        <Clock className="w-3 h-3" /> Pendiente
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Sin datos</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => downloadOne(r)}
                      disabled={
                        downloadingId === r.user_id ||
                        (onlyPending ? r.pending_batches === 0 : r.total_batches === 0)
                      }
                      data-testid={`sap-download-client-${r.user_id}`}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0B1B3D] hover:bg-[#D95A40] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-[0.1em] transition-colors"
                    >
                      {downloadingId === r.user_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      SAP
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-slate-500 mt-4">
          <strong>Regla:</strong> siempre se genera <span className="text-[#D95A40] font-bold">un archivo por cliente</span>.
          Al descargar, los lotes incluidos se marcan como &ldquo;enviados&rdquo; y desaparecerán
          del filtro <em>solo pendientes</em>.
        </p>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, icon, highlight }) {
  return (
    <div
      className={`border p-4 ${
        highlight
          ? "border-[#D95A40] bg-[#D95A40]/5"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <div className="text-[#D95A40]">{icon}</div>
      </div>
      <div className="font-display font-black text-3xl text-[#0B1B3D] tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.15em] ${className}`}
    >
      {children}
    </th>
  );
}
